/**
 * What the filter page does about a live view that stopped delivering frames.
 *
 * The shared Preview retries a failed frame five times and then stops for
 * good; a camera call that never answers stops it without even an error. On
 * the filter page that froze the guest's live view right after they paid, and
 * the operator's only way out was Exit, which threw the paid session away
 * (2026-10-03: 9 paid sessions on the client's booth, each restarted for free).
 *
 * So the page watches the time of the last frame:
 * - first stall: restart the preview (often enough when only the loop died);
 * - a second stall right after: hand over to the booth's camera recovery prompt,
 *   the same one Capture uses, and keep the session — the guest carries on once
 *   the camera is back.
 * Frames flowing again clear the strike.
 *
 * With no camera at all there is nothing to restart against: the stall goes
 * straight to the prompt. That was the common case in the field (2026-10-04:
 * in 3 of the 4 sessions that tripped the watchdog, the booth had restarted
 * and its DSLR was never reconnected before the guest paid), and the restart
 * only cost the guest another ten seconds in front of a dead preview.
 */
export type WatchdogAction = 'none' | 'restart' | 'recover'

/** No frame for this long means the live view is stuck, not slow. */
export const LIVE_VIEW_STALL_MS = 10_000

/** A DSLR's live view can take a few seconds to start; the first check waits longer. */
export const LIVE_VIEW_FIRST_FRAME_GRACE_MS = 5_000

export interface WatchdogOptions {
  /** Whether the camera store holds a device for this key at all. */
  cameraPresent?: boolean
  stallMs?: number
}

export function watchdogStep(
  now: number,
  lastFrameAt: number,
  lastActionAt: number,
  strikes: number,
  { cameraPresent = true, stallMs = LIVE_VIEW_STALL_MS }: WatchdogOptions = {}
): { action: WatchdogAction; strikes: number } {
  // Only a real frame after our last restart clears the strike; the restart's
  // own grace period must not, or a dead camera would be restarted forever.
  const recovered = lastFrameAt > lastActionAt
  const current = recovered ? 0 : strikes
  if (now - Math.max(lastFrameAt, lastActionAt) < stallMs) return { action: 'none', strikes: current }
  if (current === 0 && cameraPresent) return { action: 'restart', strikes: 1 }
  return { action: 'recover', strikes: 2 }
}

/**
 * While the reconnect prompt is up: did frames come back without its callback?
 *
 * The callback is not the only way back. Tapping the prompt with no camera
 * selected opens Settings and drops the callback, and a camera reconnected
 * from there (or one that comes back by itself) never calls it. Without this
 * the page stayed in recovery until the guest left it: countdown held,
 * watchdog off, and "camera reconnected" never logged (0 of 3 on 2026-10-04).
 */
export function framesResumedDuringRecovery(
  lastFrameAt: number,
  recoveryStartedAt: number
): boolean {
  return lastFrameAt > recoveryStartedAt
}
